import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Calendar,
  Plus,
  ArrowLeft,
  X,
  Play,
  Square,
  Archive,
  Clock,
  ChevronRight,
  Settings2,
  MessageSquare,
  HelpCircle,
  Check,
  XCircle,
  Send,
  Sunrise,
  Copy,
  CheckCheck,
  Pencil,
  Pin,
  Trash2,
  Users,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { DateTimePicker } from '@/components/DateTimePicker';
import { eventsApi, chatApi, qaApi, sourcesApi } from '@/api/client';
import { ParticipantSlotsPanel } from '@/components/events/ParticipantSlotsPanel';
import { FeedPicker, FeedLabel } from '@/components/events/FeedPicker';
import {
  OwnerOrgSelect,
  ownerOrgErrorMessage,
  useOwnerOrgPicker,
  type OwnerOrgPicker,
} from '@/components/OwnerOrgPicker';
import { useActingOrg } from '@/hooks/useActingOrg';
import {
  CREATE_PARAM,
  FEED_PARAM,
  ORG_PARAM,
  isUuid,
  resolveCreateEventPrefill,
  type CreateEventInitial,
} from '@/lib/eventFeedPrefill';
import { useAuth } from '@/contexts/AuthContext';
import { useEventChat } from '@/hooks/useEventChat';
import { AuthorName } from '@/components/events/AuthorName';
import { parseApiError } from '@/lib/apiError';
import type { JeketEvent, EventStatus, EventVisibility, QAQuestion, ChatMessage } from '@/types';

/** The event's auto-admit flag, in either spelling the API answers. */
function autoAdmitOf(event: JeketEvent): boolean {
  return (event.autoAdmitParticipants ?? event.auto_admit_participants) === true;
}

// ---- Status Badge ----

const statusColors: Record<EventStatus, string> = {
  created: 'bg-jeket-silver-muted/20 text-jeket-silver-muted',
  scheduled: 'bg-jeket-info/20 text-jeket-info',
  warmup: 'bg-yellow-500/20 text-yellow-400',
  live: 'bg-jeket-healthy/20 text-jeket-healthy',
  ended: 'bg-orange-500/20 text-orange-400',
  post_processing: 'bg-purple-500/20 text-purple-400',
  archived: 'bg-gray-500/20 text-gray-400',
};

function EventStatusBadge({ status }: { status: EventStatus }) {
  return (
    <span className={cn('px-2.5 py-0.5 rounded-full text-xs font-medium', statusColors[status])}>
      {status.replace('_', ' ')}
    </span>
  );
}

// ---- Overrun Badge ----
//
// A live/warmup event past its scheduled_end (JSS#1152). event-manager only
// auto-ends one a full grace (24 h) past its end, so a show that overruns
// keeps running; the badge says it has.

/** "Overran by 2h 5m" for a live/warmup event past scheduled_end, else null. */
export function overrunLabel(event: Pick<JeketEvent, 'status' | 'scheduled_end'>, now: number = Date.now()): string | null {
  if (event.status !== 'live' && event.status !== 'warmup') return null;
  if (!event.scheduled_end) return null;
  const end = new Date(event.scheduled_end).getTime();
  if (!Number.isFinite(end) || end >= now) return null;
  const minutes = Math.floor((now - end) / 60_000);
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `Overran by ${h > 0 ? `${h}h ${m}m` : `${m}m`}`;
}

function OverrunBadge({ event }: { event: JeketEvent }) {
  const label = overrunLabel(event);
  if (!label) return null;
  return (
    <span
      title={`Scheduled to end ${new Date(event.scheduled_end!).toLocaleString()}`}
      className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-500/20 text-red-400"
    >
      {label}
    </span>
  );
}

// ---- Event Card ----

export function EventCard({ event }: { event: JeketEvent }) {
  const navigate = useNavigate();

  return (
    <div
      onClick={() => navigate(`/audience/events/${event.id}`)}
      className="bg-jeket-black-surface border border-jeket-black-elevated rounded-xl p-5 cursor-pointer hover:border-jeket-gold/30 transition-colors group"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-jeket-black-elevated flex items-center justify-center">
            <Calendar size={18} className="text-jeket-gold" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-jeket-silver-bright group-hover:text-jeket-gold transition-colors">
              {event.name}
            </h3>
            {event.scheduled_start && (
              <p className="text-xs text-jeket-silver-muted">
                {new Date(event.scheduled_start).toLocaleString()}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <OverrunBadge event={event} />
          <EventStatusBadge status={event.status} />
        </div>
      </div>
      {event.description && (
        <p className="text-xs text-jeket-silver-muted mb-3 line-clamp-2">{event.description}</p>
      )}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {event.tags.map((tag) => (
            <span key={tag} className="px-2 py-0.5 rounded-full bg-jeket-black-elevated text-jeket-silver-muted text-[10px]">
              {tag}
            </span>
          ))}
        </div>
        <ChevronRight size={14} className="text-jeket-silver-muted group-hover:text-jeket-gold transition-colors" />
      </div>
    </div>
  );
}

// ---- Create Event Modal ----
//
// Every event has a real owner org (JSS#959): a superuser picks it, an org
// user's is implied (components/OwnerOrgPicker.tsx). A superuser in the
// all-orgs view had no picker, and since event-manager stopped writing the
// 'default' placeholder their create was refused with a raw
// `API error 400: {…}`.

export function CreateEventModal({
  open,
  onClose,
  initial,
}: {
  open: boolean;
  onClose: () => void;
  /**
   * A prefill (JSS#1182, "Use as event feed"): the feed, and the owner org a
   * superuser starts with. Keep it referentially stable while open: it is
   * applied each time the dialog opens with it.
   */
  initial?: CreateEventInitial;
}) {
  const queryClient = useQueryClient();
  const { isSuperUser } = useAuth();
  const superuser = isSuperUser();
  const picker = useOwnerOrgPicker(open, superuser);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState<'public' | 'unlisted' | 'private'>('public');
  const [scheduledStart, setScheduledStart] = useState('');
  const [durationHours, setDurationHours] = useState(1);
  const [durationMinutes, setDurationMinutes] = useState(0);
  const [chatEnabled, setChatEnabled] = useState(true);
  const [qaEnabled, setQaEnabled] = useState(true);
  const [moqNamespace, setMoqNamespace] = useState('');
  // A prefilled open (JSS#1182) sets the owner and feed. This runs after the
  // picker's own open effect (which starts at the switcher's pick), so the
  // prefill's org wins. While a prefilled dialog is open, the org settling
  // (the switcher's pick, the org list landing) is not the user's change and
  // keeps the feed; only the user's own pick clears it, below.
  const prefilled = useRef(false);
  useEffect(() => {
    if (!open) {
      prefilled.current = false;
      return;
    }
    if (!initial) return;
    prefilled.current = true;
    if (superuser && initial.orgId) picker.select(initial.orgId);
    setMoqNamespace(initial.moqNamespace ?? '');
    // `picker.select` is a state setter: stable.
  }, [open, initial, superuser]);
  // A feed is one org's source: a new owner org drops the last org's pick.
  useEffect(() => {
    if (!prefilled.current) setMoqNamespace('');
  }, [picker.orgId]);
  // The user changing the owner org always drops the feed.
  const ownerOrg: OwnerOrgPicker = {
    ...picker,
    select: (orgId: string) => {
      picker.select(orgId);
      setMoqNamespace('');
    },
  };

  const createMutation = useMutation({
    mutationFn: () =>
      eventsApi.create({
        name,
        description: description || undefined,
        visibility,
        scheduledStart: scheduledStart || undefined,
        scheduledEnd: scheduledStart
          ? new Date(new Date(scheduledStart).getTime() + (durationHours * 60 + durationMinutes) * 60000).toISOString()
          : undefined,
        config: { chatEnabled, qaEnabled },
        moqNamespace: moqNamespace.trim() || undefined,
        // An org user's org is implied: they send none.
        orgId: ownerOrg.orgId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      closeModal();
      setName('');
      setDescription('');
      setVisibility('public');
      setScheduledStart('');
      setDurationHours(1);
      setDurationMinutes(0);
      setChatEnabled(true);
      setQaEnabled(true);
      setMoqNamespace('');
    },
  });

  // A reopened modal does not show the last attempt's refusal.
  function closeModal() {
    createMutation.reset();
    onClose();
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={closeModal} />
      <div className="relative bg-jeket-black-surface border border-jeket-gold/20 rounded-xl w-full max-w-lg p-6 shadow-jeket">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-jeket-silver-bright">Create Event</h2>
          <button onClick={closeModal} aria-label="Close" className="text-jeket-silver-muted hover:text-jeket-silver-bright">
            <X size={18} />
          </button>
        </div>

        <div className="space-y-4">
          <OwnerOrgSelect
            id="create-event-owner-org"
            picker={ownerOrg}
            noun="event"
            labelClassName="block text-xs font-medium text-jeket-silver-muted mb-1"
            selectClassName="w-full bg-jeket-black-elevated border border-jeket-gold/20 rounded-lg px-3 py-2 text-sm text-jeket-silver-bright focus:outline-none focus:border-jeket-gold/50"
            hintClassName="mt-1 text-[11px] text-jeket-silver-muted/70"
          />

          {/* Name */}
          <div>
            <label className="block text-xs font-medium text-jeket-silver-muted mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Event name"
              className="w-full bg-jeket-black-elevated border border-jeket-gold/20 rounded-lg px-3 py-2 text-sm text-jeket-silver-bright placeholder:text-jeket-silver-muted/50 focus:outline-none focus:border-jeket-gold/50"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-jeket-silver-muted mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Event description"
              rows={3}
              className="w-full bg-jeket-black-elevated border border-jeket-gold/20 rounded-lg px-3 py-2 text-sm text-jeket-silver-bright placeholder:text-jeket-silver-muted/50 focus:outline-none focus:border-jeket-gold/50 resize-none"
            />
          </div>

          {/* Visibility */}
          <div>
            <label className="block text-xs font-medium text-jeket-silver-muted mb-1">Visibility</label>
            <select
              value={visibility}
              onChange={(e) => setVisibility(e.target.value as 'public' | 'unlisted' | 'private')}
              className="w-full bg-jeket-black-elevated border border-jeket-gold/20 rounded-lg px-3 py-2 text-sm text-jeket-silver-bright focus:outline-none focus:border-jeket-gold/50"
            >
              <option value="public">Public</option>
              <option value="unlisted">Unlisted</option>
              <option value="private">Private</option>
            </select>
          </div>

          {/* Schedule */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-jeket-silver-muted mb-1">Start Time</label>
              <DateTimePicker
                value={scheduledStart}
                onChange={setScheduledStart}
                placeholder="Select date & time"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-jeket-silver-muted mb-1">Duration</label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={0}
                  max={72}
                  value={durationHours}
                  onChange={(e) => setDurationHours(Math.max(0, Number(e.target.value)))}
                  className="w-16 bg-jeket-black-elevated border border-jeket-gold/20 rounded-lg px-3 py-2 text-sm text-jeket-silver-bright text-center focus:outline-none focus:border-jeket-gold/50"
                />
                <span className="text-xs text-jeket-silver-muted">hr</span>
                <input
                  type="number"
                  min={0}
                  max={59}
                  step={5}
                  value={durationMinutes}
                  onChange={(e) => setDurationMinutes(Math.min(59, Math.max(0, Number(e.target.value))))}
                  className="w-16 bg-jeket-black-elevated border border-jeket-gold/20 rounded-lg px-3 py-2 text-sm text-jeket-silver-bright text-center focus:outline-none focus:border-jeket-gold/50"
                />
                <span className="text-xs text-jeket-silver-muted">min</span>
              </div>
            </div>
          </div>

          {/* Toggles */}
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={chatEnabled}
                onChange={(e) => setChatEnabled(e.target.checked)}
                className="rounded border-jeket-gold/30 bg-jeket-black-elevated text-jeket-gold focus:ring-jeket-gold/50"
              />
              <span className="text-sm text-jeket-silver-bright">Chat</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={qaEnabled}
                onChange={(e) => setQaEnabled(e.target.checked)}
                className="rounded border-jeket-gold/30 bg-jeket-black-elevated text-jeket-gold focus:ring-jeket-gold/50"
              />
              <span className="text-sm text-jeket-silver-bright">Q&A</span>
            </label>
          </div>

          {/* Feed (JSS#1159): one of the owner org's sources, stored as moqNamespace. */}
          <div>
            <label htmlFor="create-event-feed" className="block text-xs font-medium text-jeket-silver-muted mb-1">
              Feed <span className="text-jeket-silver-muted/60">(optional)</span>
            </label>
            <FeedPicker
              id="create-event-feed"
              value={moqNamespace}
              onChange={setMoqNamespace}
              orgId={ownerOrg.orgId}
              orgPending={ownerOrg.missing}
              inputClassName="w-full bg-jeket-black-elevated border border-jeket-gold/20 rounded-lg px-3 py-2 text-sm text-jeket-silver-bright placeholder:text-jeket-silver-muted/50 focus:outline-none focus:border-jeket-gold/50"
            />
            <p className="mt-1 text-[11px] text-jeket-silver-muted/70">
              The source the viewer page plays. With no feed it uses the build-time fallback.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={closeModal}
            className="px-4 py-2 text-sm text-jeket-silver-muted hover:text-jeket-silver-bright transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => createMutation.mutate()}
            disabled={!name.trim() || ownerOrg.missing || createMutation.isPending}
            className="px-4 py-2 text-sm font-medium bg-jeket-gold/20 text-jeket-gold rounded-lg hover:bg-jeket-gold/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {createMutation.isPending ? 'Creating...' : 'Create Event'}
          </button>
        </div>

        {createMutation.isError && (
          <p role="alert" className="mt-3 text-xs text-red-400">
            {ownerOrgErrorMessage(createMutation.error, 'event')}
          </p>
        )}
      </div>
    </div>
  );
}

// ---- Events List ----

type FilterTab = 'all' | 'upcoming' | 'live' | 'ended' | 'archived';

function EventsList() {
  const [filter, setFilter] = useState<FilterTab>('all');
  const [showCreate, setShowCreate] = useState(false);
  const [createInitial, setCreateInitial] = useState<CreateEventInitial | undefined>();
  useCreateFromLink(setCreateInitial, () => setShowCreate(true));

  const queryParams = (() => {
    switch (filter) {
      case 'upcoming': return { upcoming: 'true' };
      case 'live': return { status: 'live' };
      case 'ended': return { status: 'ended' };
      case 'archived': return { status: 'archived' };
      default: return undefined;
    }
  })();

  const { data: events = [], isLoading } = useQuery({
    queryKey: ['events', filter],
    queryFn: () => eventsApi.list(queryParams),
  });

  const tabs: { key: FilterTab; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'upcoming', label: 'Upcoming' },
    { key: 'live', label: 'Live' },
    { key: 'ended', label: 'Ended' },
    { key: 'archived', label: 'Archived' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Calendar size={24} className="text-jeket-gold" />
          <h1 className="text-xl font-bold text-jeket-silver-bright">Events</h1>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-jeket-gold/20 text-jeket-gold rounded-lg hover:bg-jeket-gold/30 transition-colors"
        >
          <Plus size={16} />
          Create Event
        </button>
      </div>

      {/* Filter tabs */}
      <div className="flex items-center gap-1 border-b border-jeket-black-elevated">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
              filter === tab.key
                ? 'border-jeket-gold text-jeket-gold'
                : 'border-transparent text-jeket-silver-muted hover:text-jeket-silver-bright'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Events grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-jeket-gold" />
        </div>
      ) : events.length === 0 ? (
        <div className="text-center py-12">
          <Calendar size={48} className="mx-auto text-jeket-silver-muted/30 mb-3" />
          <p className="text-jeket-silver-muted">No events found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {events.map((event) => (
            <EventCard key={event.id} event={event} />
          ))}
        </div>
      )}

      <CreateEventModal
        open={showCreate}
        initial={createInitial}
        onClose={() => {
          setShowCreate(false);
          // The prefill was for that one open: the button opens a blank dialog.
          setCreateInitial(undefined);
        }}
      />
    </div>
  );
}

/**
 * `?create=1&feed=<sourceId>&org=<orgId>` (JSS#1182, the Production page's
 * "Use as event feed") opens Create Event prefilled. The params are checked
 * (lib/eventFeedPrefill.ts) once the org list, and for a feed the sources
 * list, have answered; then the dialog opens and the params are stripped
 * with `replace`, so Back and a reload do not reopen it.
 */
function useCreateFromLink(setInitial: (initial: CreateEventInitial) => void, openDialog: () => void) {
  const [searchParams, setSearchParams] = useSearchParams();
  const wanted = searchParams.get(CREATE_PARAM) === '1';
  const feed = searchParams.get(FEED_PARAM);
  const org = searchParams.get(ORG_PARAM);
  const needSources = wanted && isUuid(feed);

  const { superuser, organizations, actingOrgId, actingOrgPending } = useActingOrg(wanted);
  const sources = useQuery({
    queryKey: ['sources'],
    queryFn: () => sourcesApi.list(),
    enabled: needSources,
    staleTime: 30_000,
  });
  const ready = wanted && !organizations.isPending && !actingOrgPending && (!needSources || !sources.isPending);

  useEffect(() => {
    if (!ready) return;
    setInitial(
      resolveCreateEventPrefill({
        feed,
        org,
        superuser,
        orgs: organizations.data,
        actingOrgId,
        sources: sources.data,
      }),
    );
    openDialog();
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(CREATE_PARAM);
        next.delete(FEED_PARAM);
        next.delete(ORG_PARAM);
        return next;
      },
      { replace: true },
    );
    // Runs once per link: stripping the params makes `ready` false.
  }, [ready]);
}

// ---- Event Detail ----

// ---- Editable Config Form ----

/**
 * An instant as the DateTimePicker's local `YYYY-MM-DDTHH:mm`. The form used
 * the UTC `toISOString().slice(0, 16)`, which is read back as local time:
 * every save would have moved the schedule by the UTC offset (latent while
 * the save's snake_case schedule keys were ignored; JSS#1152).
 */
function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

export function EditableConfigTab({ event, onSave }: { event: JeketEvent; onSave: () => void }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(event.name);
  const [description, setDescription] = useState(event.description);
  const [visibility, setVisibility] = useState<EventVisibility>(event.visibility);
  const [scheduledStart, setScheduledStart] = useState(
    event.scheduled_start ? toLocalInput(event.scheduled_start) : ''
  );
  const [durationHours, setDurationHours] = useState(() => {
    if (!event.scheduled_start || !event.scheduled_end) return 1;
    const ms = new Date(event.scheduled_end).getTime() - new Date(event.scheduled_start).getTime();
    return Math.floor(ms / 3600000);
  });
  const [durationMinutes, setDurationMinutes] = useState(() => {
    if (!event.scheduled_start || !event.scheduled_end) return 0;
    const ms = new Date(event.scheduled_end).getTime() - new Date(event.scheduled_start).getTime();
    return Math.floor((ms % 3600000) / 60000);
  });
  const [chatEnabled, setChatEnabled] = useState(event.config.chatEnabled);
  const [qaEnabled, setQaEnabled] = useState(event.config.qaEnabled);
  const [qaModerated, setQaModerated] = useState(event.config.qaModerated);
  const [anonymousNames, setAnonymousNames] = useState(event.config.anonymousNames === true);
  const [warmupMinutes, setWarmupMinutes] = useState(event.config.warmupMinutes);
  const [autoAdmit, setAutoAdmit] = useState(autoAdmitOf(event));
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>(event.tags);
  const [moqNamespace, setMoqNamespace] = useState(event.moq_namespace ?? '');
  // No scheduled end: the event is never auto-ended (JSS#1152). Settable
  // while live too, which is when it matters: a show running long.
  const [noEnd, setNoEnd] = useState(!event.scheduled_end);

  // Editing is gated by lifecycle:
  //   - `created` / `scheduled` — full edit access (pre-event setup).
  //   - `warmup` / `live`       — limited edit (name, description, tags,
  //                               moq_namespace). Schedule + pre-event
  //                               toggles are still locked because they
  //                               can desync the running event timing.
  //   - `ended` / `archived`    — read-only.
  // `canEdit` controls the Edit button's visibility; per-field locks
  // below disable the schedule inputs while live so the slug-changing
  // and timing-changing fields stay safe.
  const canEdit = ['created', 'scheduled', 'warmup', 'live'].includes(event.status);
  const isLockedFieldsEditable = ['created', 'scheduled'].includes(event.status);

  const updateMutation = useMutation({
    mutationFn: () => {
      const scheduledEnd = scheduledStart && !noEnd
        ? new Date(new Date(scheduledStart).getTime() + (durationHours * 60 + durationMinutes) * 60000).toISOString()
        : null;
      // Schedule fields only included when editing them is allowed —
      // otherwise we'd echo the existing values redundantly. The API
      // keeps a field it is not sent; `scheduledEnd: null` clears the end.
      // While live, the only schedule change is clearing the end.
      const schedulePayload = isLockedFieldsEditable
        ? {
            scheduledStart: scheduledStart ? new Date(scheduledStart).toISOString() : null,
            scheduledEnd,
          }
        : noEnd && event.scheduled_end
          ? { scheduledEnd: null }
          : {};
      return eventsApi.update(event.id, {
        name,
        description,
        visibility,
        ...schedulePayload,
        config: { chatEnabled, qaEnabled, qaModerated, anonymousNames, chatMaxLength: event.config.chatMaxLength, chatRateLimit: event.config.chatRateLimit, warmupMinutes },
        // Only when changed: an answer that left the flag out must not turn it off on the next save.
        ...(autoAdmit !== autoAdmitOf(event) ? { autoAdmitParticipants: autoAdmit } : {}),
        tags,
        // Send the trimmed value, or null when cleared, so the API
        // distinguishes "no change" (omitted) from "clear" (null).
        moqNamespace: moqNamespace.trim() === '' ? null : moqNamespace.trim(),
      });
    },
    onSuccess: () => {
      setEditing(false);
      onSave();
    },
  });

  function cancelEdit() {
    setEditing(false);
    setName(event.name);
    setDescription(event.description);
    setVisibility(event.visibility);
    setScheduledStart(event.scheduled_start ? toLocalInput(event.scheduled_start) : '');
    setChatEnabled(event.config.chatEnabled);
    setQaEnabled(event.config.qaEnabled);
    setQaModerated(event.config.qaModerated);
    setAnonymousNames(event.config.anonymousNames === true);
    setWarmupMinutes(event.config.warmupMinutes);
    setAutoAdmit(autoAdmitOf(event));
    setTags(event.tags);
    setMoqNamespace(event.moq_namespace ?? '');
    setNoEnd(!event.scheduled_end);
    if (event.scheduled_start && event.scheduled_end) {
      const ms = new Date(event.scheduled_end).getTime() - new Date(event.scheduled_start).getTime();
      setDurationHours(Math.floor(ms / 3600000));
      setDurationMinutes(Math.floor((ms % 3600000) / 60000));
    }
  }

  function addTag() {
    const t = tagInput.trim().toLowerCase();
    if (t && !tags.includes(t)) {
      setTags([...tags, t]);
    }
    setTagInput('');
  }

  const inputClass = 'w-full bg-jeket-black-elevated border border-jeket-gold/20 rounded-lg px-3 py-2 text-sm text-jeket-silver-bright placeholder:text-jeket-silver-muted/50 focus:outline-none focus:border-jeket-gold/50';

  return (
    <div className="bg-jeket-black-surface border border-jeket-black-elevated rounded-xl p-6 space-y-4">
      {/* Edit / Save / Cancel header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-jeket-silver-muted">Event Configuration</h3>
        {canEdit && !editing && (
          <button
            onClick={() => setEditing(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-jeket-gold/20 text-jeket-gold rounded-lg hover:bg-jeket-gold/30 transition-colors"
          >
            <Pencil size={12} /> Edit
          </button>
        )}
        {editing && (
          <div className="flex items-center gap-2">
            <button
              onClick={cancelEdit}
              className="px-3 py-1.5 text-xs text-jeket-silver-muted hover:text-jeket-silver-bright transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => updateMutation.mutate()}
              disabled={!name.trim() || updateMutation.isPending}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-jeket-gold/20 text-jeket-gold rounded-lg hover:bg-jeket-gold/30 disabled:opacity-50 transition-colors"
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>

      {/* The server's message (`Stream not found`, …), not the raw
          `API error 404: {…}` (JSS#959). */}
      {updateMutation.isError && (
        <p role="alert" className="text-xs text-red-400">{parseApiError(updateMutation.error).message}</p>
      )}

      <div className="grid grid-cols-2 gap-6">
        <div>
          <label className="block text-xs font-medium text-jeket-silver-muted mb-1">Name</label>
          {editing ? (
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} className={inputClass} />
          ) : (
            <p className="text-sm text-jeket-silver-bright">{event.name}</p>
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-jeket-silver-muted mb-1">Visibility</label>
          {editing ? (
            <select value={visibility} onChange={(e) => setVisibility(e.target.value as EventVisibility)} className={inputClass}>
              <option value="public">Public</option>
              <option value="unlisted">Unlisted</option>
              <option value="private">Private</option>
            </select>
          ) : (
            <p className="text-sm text-jeket-silver-bright capitalize">{event.visibility}</p>
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-jeket-silver-muted mb-1">
            Start Time {editing && !isLockedFieldsEditable && <span className="text-jeket-silver-muted/60">(locked while live)</span>}
          </label>
          {editing && isLockedFieldsEditable ? (
            <DateTimePicker value={scheduledStart} onChange={setScheduledStart} placeholder="Select date & time" />
          ) : (
            <p className="text-sm text-jeket-silver-bright">
              {event.scheduled_start ? new Date(event.scheduled_start).toLocaleString() : 'Not set'}
            </p>
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-jeket-silver-muted mb-1">
            Duration {editing && !isLockedFieldsEditable && <span className="text-jeket-silver-muted/60">(locked while live)</span>}
          </label>
          {editing && (
            <label className="flex items-center gap-2 mb-2 text-xs text-jeket-silver-muted">
              <input
                type="checkbox"
                checked={noEnd}
                onChange={(e) => setNoEnd(e.target.checked)}
                // While live, an end can be cleared but not set back.
                disabled={!isLockedFieldsEditable && !event.scheduled_end}
              />
              No scheduled end (never auto-ended)
            </label>
          )}
          {editing && isLockedFieldsEditable && !noEnd ? (
            <div className="flex items-center gap-2">
              <input
                type="number" min={0} max={72} value={durationHours}
                onChange={(e) => setDurationHours(Math.max(0, Number(e.target.value)))}
                className="w-16 bg-jeket-black-elevated border border-jeket-gold/20 rounded-lg px-3 py-2 text-sm text-jeket-silver-bright text-center focus:outline-none focus:border-jeket-gold/50"
              />
              <span className="text-xs text-jeket-silver-muted">hr</span>
              <input
                type="number" min={0} max={59} step={5} value={durationMinutes}
                onChange={(e) => setDurationMinutes(Math.min(59, Math.max(0, Number(e.target.value))))}
                className="w-16 bg-jeket-black-elevated border border-jeket-gold/20 rounded-lg px-3 py-2 text-sm text-jeket-silver-bright text-center focus:outline-none focus:border-jeket-gold/50"
              />
              <span className="text-xs text-jeket-silver-muted">min</span>
            </div>
          ) : (
            <p className="text-sm text-jeket-silver-bright">
              {event.scheduled_start && event.scheduled_end
                ? (() => {
                    const ms = new Date(event.scheduled_end).getTime() - new Date(event.scheduled_start).getTime();
                    const h = Math.floor(ms / 3600000);
                    const m = Math.floor((ms % 3600000) / 60000);
                    return `${h}h ${m}m`;
                  })()
                : 'Not set'}
            </p>
          )}
        </div>
        <div className="col-span-2">
          <label className="block text-xs font-medium text-jeket-silver-muted mb-1">Description</label>
          {editing ? (
            <textarea
              value={description} onChange={(e) => setDescription(e.target.value)}
              rows={3} placeholder="Event description"
              className={cn(inputClass, 'resize-none')}
            />
          ) : (
            <p className="text-sm text-jeket-silver-bright">{event.description || 'No description'}</p>
          )}
        </div>
        <div className="col-span-2">
          <label htmlFor="edit-event-feed" className="block text-xs font-medium text-jeket-silver-muted mb-1">
            Feed <span className="text-jeket-silver-muted/60">(optional)</span>
          </label>
          {editing ? (
            <>
              <FeedPicker
                id="edit-event-feed"
                value={moqNamespace}
                onChange={setMoqNamespace}
                orgId={event.org_id || undefined}
                inputClassName={inputClass}
              />
              <p className="mt-1 text-[11px] text-jeket-silver-muted/70">
                The source the viewer page plays. With no feed it falls back to the build-time default.
              </p>
            </>
          ) : (
            <p className="text-sm text-jeket-silver-bright">
              <FeedLabel namespace={event.moq_namespace} orgId={event.org_id || undefined} />
            </p>
          )}
        </div>
      </div>

      <div className="border-t border-jeket-black-elevated pt-4">
        <h3 className="text-xs font-medium text-jeket-silver-muted mb-3">Configuration</h3>
        {editing ? (
          <div className="grid grid-cols-2 gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={chatEnabled} onChange={(e) => setChatEnabled(e.target.checked)}
                className="rounded border-jeket-gold/30 bg-jeket-black-elevated text-jeket-gold focus:ring-jeket-gold/50" />
              <span className="text-sm text-jeket-silver-bright">Chat</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={qaEnabled} onChange={(e) => setQaEnabled(e.target.checked)}
                className="rounded border-jeket-gold/30 bg-jeket-black-elevated text-jeket-gold focus:ring-jeket-gold/50" />
              <span className="text-sm text-jeket-silver-bright">Q&A</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={qaModerated} onChange={(e) => setQaModerated(e.target.checked)}
                className="rounded border-jeket-gold/30 bg-jeket-black-elevated text-jeket-gold focus:ring-jeket-gold/50" />
              <span className="text-sm text-jeket-silver-bright">Q&A Moderated</span>
            </label>
            <label
              className="flex items-center gap-2 cursor-pointer"
              title="Signed-out viewers may type a name for chat and Q&A. It is shown marked unverified."
            >
              <input type="checkbox" checked={anonymousNames} onChange={(e) => setAnonymousNames(e.target.checked)}
                className="rounded border-jeket-gold/30 bg-jeket-black-elevated text-jeket-gold focus:ring-jeket-gold/50" />
              <span className="text-sm text-jeket-silver-bright">Anonymous names</span>
            </label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-jeket-silver-bright">Warmup</span>
              <input
                type="number" min={0} max={60} value={warmupMinutes}
                onChange={(e) => setWarmupMinutes(Math.max(0, Number(e.target.value)))}
                className="w-16 bg-jeket-black-elevated border border-jeket-gold/20 rounded-lg px-2 py-1.5 text-sm text-jeket-silver-bright text-center focus:outline-none focus:border-jeket-gold/50"
              />
              <span className="text-xs text-jeket-silver-muted">min</span>
            </div>
            <label
              className="flex items-center gap-2 cursor-pointer"
              title="Guests are admitted as they ask to join. Putting one on a slot is still the producer's Take."
            >
              <input type="checkbox" checked={autoAdmit} onChange={(e) => setAutoAdmit(e.target.checked)}
                className="rounded border-jeket-gold/30 bg-jeket-black-elevated text-jeket-gold focus:ring-jeket-gold/50" />
              <span className="text-sm text-jeket-silver-bright">Admit guests automatically</span>
            </label>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-4">
            <div className="flex items-center gap-2">
              <span className={cn('w-2 h-2 rounded-full', event.config.chatEnabled ? 'bg-jeket-healthy' : 'bg-jeket-silver-muted/30')} />
              <span className="text-sm text-jeket-silver-bright">Chat</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={cn('w-2 h-2 rounded-full', event.config.qaEnabled ? 'bg-jeket-healthy' : 'bg-jeket-silver-muted/30')} />
              <span className="text-sm text-jeket-silver-bright">Q&A</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={cn('w-2 h-2 rounded-full', event.config.qaModerated ? 'bg-jeket-healthy' : 'bg-jeket-silver-muted/30')} />
              <span className="text-sm text-jeket-silver-bright">Q&A Moderated</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-jeket-silver-bright">
              Warmup: {event.config.warmupMinutes}min
            </div>
            <div className="flex items-center gap-2">
              <span className={cn('w-2 h-2 rounded-full', autoAdmitOf(event) ? 'bg-jeket-healthy' : 'bg-jeket-silver-muted/30')} />
              <span className="text-sm text-jeket-silver-bright">Admit guests automatically</span>
            </div>
          </div>
        )}
      </div>

      {/* Tags */}
      <div className="border-t border-jeket-black-elevated pt-4">
        <h3 className="text-xs font-medium text-jeket-silver-muted mb-2">Tags</h3>
        {editing ? (
          <div>
            <div className="flex flex-wrap gap-2 mb-2">
              {tags.map((tag) => (
                <span key={tag} className="flex items-center gap-1 px-2 py-1 rounded bg-jeket-black-elevated text-xs text-jeket-silver-bright">
                  {tag}
                  <button onClick={() => setTags(tags.filter((t) => t !== tag))} className="text-jeket-silver-muted hover:text-red-400">
                    <X size={10} />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text" value={tagInput} onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                placeholder="Add tag..."
                className="flex-1 bg-jeket-black-elevated border border-jeket-gold/20 rounded-lg px-3 py-1.5 text-xs text-jeket-silver-bright placeholder:text-jeket-silver-muted/50 focus:outline-none focus:border-jeket-gold/50"
              />
              <button onClick={addTag} className="px-3 py-1.5 text-xs bg-jeket-gold/20 text-jeket-gold rounded-lg hover:bg-jeket-gold/30">
                Add
              </button>
            </div>
          </div>
        ) : tags.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {tags.map((tag) => (
              <span key={tag} className="px-2 py-1 rounded bg-jeket-black-elevated text-xs text-jeket-silver-bright">
                {tag}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-xs text-jeket-silver-muted">No tags</p>
        )}
      </div>
    </div>
  );
}

// ---- Admin Chat Tab (WebSocket) ----

function AdminChatTab({ event }: { event: JeketEvent }) {
  const queryClient = useQueryClient();
  const chatEndRef = useRef<HTMLDivElement>(null);
  const [messageInput, setMessageInput] = useState('');
  const [systemMsgInput, setSystemMsgInput] = useState('');

  const isLiveOrWarmup = ['warmup', 'live'].includes(event.status);

  // Use WebSocket for live/warmup events, REST polling otherwise
  // The server assigns the author: the signed-in moderator (JSS#1191).
  const { messages: wsMessages, sendMessage, isConnected, viewerCount } = useEventChat({
    eventId: event.id,
    enabled: isLiveOrWarmup,
  });

  // REST fallback for non-live events
  const { data: restMessages = [] } = useQuery({
    queryKey: ['chat-messages', event.id],
    queryFn: () => chatApi.getMessages(event.id, { limit: 100 }),
    enabled: !isLiveOrWarmup,
    refetchInterval: 5000,
  });

  const messages = isLiveOrWarmup ? wsMessages : restMessages;

  const pinMutation = useMutation({
    mutationFn: ({ id, pinned }: { id: string; pinned: boolean }) => chatApi.pinMessage(id, pinned),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => chatApi.deleteMessage(id),
  });

  const systemMsgMutation = useMutation({
    // Posted as the signed-in moderator: the server sets the author (JSS#1191).
    mutationFn: (content: string) => chatApi.sendMessage(event.id, { content }),
    onSuccess: () => setSystemMsgInput(''),
  });

  const clearChatMutation = useMutation({
    mutationFn: () => chatApi.clearAllMessages(event.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['chat-messages', event.id] }),
  });

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  function handleSend() {
    const content = messageInput.trim();
    if (!content) return;
    if (isLiveOrWarmup && isConnected) {
      sendMessage(content);
    }
    setMessageInput('');
  }

  return (
    <div className="bg-jeket-black-surface border border-jeket-black-elevated rounded-xl overflow-hidden">
      {/* Connection status for live events */}
      {isLiveOrWarmup && (
        <div className="flex items-center justify-between px-4 py-2 border-b border-jeket-black-elevated">
          <div className="flex items-center gap-2">
            <span className={cn('w-2 h-2 rounded-full', isConnected ? 'bg-jeket-healthy' : 'bg-red-400')} />
            <span className="text-xs text-jeket-silver-muted">
              {isConnected ? 'Connected' : 'Reconnecting...'}
            </span>
          </div>
          <span className="text-xs text-jeket-silver-muted">{viewerCount} viewer{viewerCount !== 1 ? 's' : ''}</span>
        </div>
      )}

      {/* Messages */}
      <div className="max-h-[400px] overflow-y-auto p-4 space-y-2">
        {messages.length === 0 ? (
          <div className="text-center py-12">
            <MessageSquare size={48} className="mx-auto text-jeket-silver-muted/30 mb-3" />
            <p className="text-jeket-silver-muted">No messages yet</p>
          </div>
        ) : (
          messages.map((msg: ChatMessage) => (
            <div key={msg.id} className={cn('group flex items-start gap-2 text-sm', msg.deleted && 'opacity-40')}>
              <div className="flex-1">
                {msg.pinned && <Pin size={10} className="inline text-jeket-gold mr-1" />}
                <AuthorName
                  name={msg.username}
                  verified={msg.verified}
                  className={cn('font-medium', msg.type === 'system' ? 'text-jeket-info italic' : 'text-jeket-gold')}
                />{' '}
                <span className="text-jeket-silver-bright">{msg.content}</span>
                <span className="text-xs text-jeket-silver-muted ml-2">
                  {new Date(msg.created_at).toLocaleTimeString()}
                </span>
              </div>
              {!msg.deleted && (
                <div className="hidden group-hover:flex items-center gap-0.5 shrink-0">
                  <button
                    onClick={() => pinMutation.mutate({ id: msg.id, pinned: !msg.pinned })}
                    className={cn('p-1 rounded transition-colors', msg.pinned ? 'text-jeket-gold' : 'text-jeket-silver-muted hover:text-jeket-gold')}
                    title={msg.pinned ? 'Unpin' : 'Pin'}
                  >
                    <Pin size={12} />
                  </button>
                  <button
                    onClick={() => deleteMutation.mutate(msg.id)}
                    className="p-1 text-jeket-silver-muted hover:text-red-400 rounded transition-colors"
                    title="Delete"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              )}
            </div>
          ))
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Input area */}
      {isLiveOrWarmup && (
        <div className="border-t border-jeket-black-elevated p-3 space-y-2">
          {/* Chat message */}
          <div className="flex gap-2">
            <input
              type="text" value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSend(); }}
              placeholder="Send as Admin..."
              className="flex-1 bg-jeket-black-elevated border border-jeket-gold/20 rounded-lg px-3 py-1.5 text-sm text-jeket-silver-bright placeholder:text-jeket-silver-muted/50 focus:outline-none focus:border-jeket-gold/50"
            />
            <button onClick={handleSend} disabled={!messageInput.trim() || !isConnected}
              className="px-3 py-1.5 text-sm bg-jeket-gold/20 text-jeket-gold rounded-lg hover:bg-jeket-gold/30 disabled:opacity-50 transition-colors">
              <Send size={14} />
            </button>
          </div>
          {/* System message */}
          <div className="flex gap-2">
            <input
              type="text" value={systemMsgInput}
              onChange={(e) => setSystemMsgInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && systemMsgInput.trim()) systemMsgMutation.mutate(systemMsgInput.trim()); }}
              placeholder="System announcement..."
              className="flex-1 bg-jeket-black-elevated border border-jeket-info/20 rounded-lg px-3 py-1.5 text-sm text-jeket-silver-bright placeholder:text-jeket-silver-muted/50 focus:outline-none focus:border-jeket-info/50"
            />
            <button
              onClick={() => { if (systemMsgInput.trim()) systemMsgMutation.mutate(systemMsgInput.trim()); }}
              disabled={!systemMsgInput.trim() || systemMsgMutation.isPending}
              className="px-3 py-1.5 text-xs bg-jeket-info/20 text-jeket-info rounded-lg hover:bg-jeket-info/30 disabled:opacity-50 transition-colors"
            >
              System
            </button>
            <button
              onClick={() => { if (window.confirm('Clear all chat messages for this event?')) clearChatMutation.mutate(); }}
              disabled={clearChatMutation?.isPending}
              className="px-3 py-1.5 text-xs bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 disabled:opacity-50 transition-colors"
            >
              Clear All
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Shareable URL ----

function ShareableUrl({ slug }: { slug: string }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/event/${slug}`;

  function handleCopy() {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-jeket-silver-muted font-mono truncate">/event/{slug}</span>
      <button onClick={handleCopy} className="p-1 text-jeket-silver-muted hover:text-jeket-gold transition-colors" title="Copy viewer URL">
        {copied ? <CheckCheck size={13} className="text-jeket-healthy" /> : <Copy size={13} />}
      </button>
    </div>
  );
}

// ---- Event Detail ----

export function EventDetail({ eventId }: { eventId: string }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'config' | 'participants' | 'qa' | 'chat'>('config');
  const [answeringId, setAnsweringId] = useState<string | null>(null);
  const [answerText, setAnswerText] = useState('');
  // A refused lifecycle action (schedule, go live, end, …) or delete showed
  // nothing at all (JSS#1060): the button stopped spinning and the status
  // stayed. One line, the last action's, with the server's message
  // (`Invalid transition from 'ended' to 'live'`, `Event not found`, …), never
  // the raw `API error 400: {…}`.
  const [actionError, setActionError] = useState<string | null>(null);
  useEffect(() => setActionError(null), [eventId]);

  const { data: event, isLoading } = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => eventsApi.get(eventId),
  });

  const { data: questions = [] } = useQuery({
    queryKey: ['qa-questions', eventId],
    queryFn: () => qaApi.list(eventId),
    enabled: activeTab === 'qa',
    refetchInterval: activeTab === 'qa' ? 5000 : false,
  });

  const invalidateEvent = () => {
    queryClient.invalidateQueries({ queryKey: ['event', eventId] });
    queryClient.invalidateQueries({ queryKey: ['events'] });
  };

  // Each action clears the last refusal when it starts. A refusal also
  // refetches the event: an invalid transition usually means the page's
  // status is stale (another tab, the scheduler), and the buttons follow it.
  const lifecycle = (label: string) => ({
    onMutate: () => setActionError(null),
    onError: (err: unknown) => {
      setActionError(`${label} failed: ${parseApiError(err).message}`);
      invalidateEvent();
    },
  });

  const scheduleMutation = useMutation({
    mutationFn: () => eventsApi.schedule(eventId),
    onSuccess: invalidateEvent,
    ...lifecycle('Schedule'),
  });

  const warmupMutation = useMutation({
    mutationFn: () => eventsApi.warmup(eventId),
    onSuccess: invalidateEvent,
    ...lifecycle('Warmup'),
  });

  const goLiveMutation = useMutation({
    mutationFn: () => eventsApi.goLive(eventId),
    onSuccess: invalidateEvent,
    ...lifecycle('Go Live'),
  });

  const endMutation = useMutation({
    mutationFn: () => eventsApi.end(eventId),
    onSuccess: invalidateEvent,
    ...lifecycle('End'),
  });

  const archiveMutation = useMutation({
    mutationFn: () => eventsApi.archive(eventId),
    onSuccess: invalidateEvent,
    ...lifecycle('Archive'),
  });

  const backToDraftMutation = useMutation({
    mutationFn: () => eventsApi.unschedule(eventId),
    onSuccess: invalidateEvent,
    ...lifecycle('Back to Draft'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => eventsApi.delete(eventId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      navigate('/audience/events');
    },
    ...lifecycle('Delete'),
  });

  const approveMutation = useMutation({
    mutationFn: (id: string) => qaApi.approve(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['qa-questions', eventId] }),
  });

  const dismissMutation = useMutation({
    mutationFn: (id: string) => qaApi.dismiss(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['qa-questions', eventId] }),
  });

  const answerMutation = useMutation({
    mutationFn: ({ id, answer }: { id: string; answer: string }) =>
      // The answerer is the signed-in moderator, set by the server (JSS#1191).
      qaApi.answer(id, { answer }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qa-questions', eventId] });
      setAnsweringId(null);
      setAnswerText('');
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-jeket-gold" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="text-center py-12">
        <p className="text-jeket-silver-muted">Event not found</p>
      </div>
    );
  }

  // Lifecycle actions based on status
  const lifecycleActions = (() => {
    switch (event.status) {
      case 'created':
        return [
          { label: 'Schedule', icon: Clock, mutation: scheduleMutation, variant: 'info' as const },
          { label: 'Go Live', icon: Play, mutation: goLiveMutation, variant: 'success' as const },
        ];
      case 'scheduled':
        return [
          { label: 'Warmup', icon: Sunrise, mutation: warmupMutation, variant: 'warning' as const },
          { label: 'Go Live', icon: Play, mutation: goLiveMutation, variant: 'success' as const },
          { label: 'Back to Draft', icon: ArrowLeft, mutation: backToDraftMutation, variant: 'muted' as const },
        ];
      case 'warmup':
        return [
          { label: 'Go Live', icon: Play, mutation: goLiveMutation, variant: 'success' as const },
          { label: 'End', icon: Square, mutation: endMutation, variant: 'danger' as const },
        ];
      case 'live':
        return [
          { label: 'End', icon: Square, mutation: endMutation, variant: 'danger' as const },
        ];
      case 'ended':
        return [
          { label: 'Archive', icon: Archive, mutation: archiveMutation, variant: 'muted' as const },
        ];
      default:
        return [];
    }
  })();

  const actionVariantStyles = {
    info: 'bg-jeket-info/20 text-jeket-info hover:bg-jeket-info/30',
    success: 'bg-jeket-healthy/20 text-jeket-healthy hover:bg-jeket-healthy/30',
    warning: 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30',
    danger: 'bg-red-500/20 text-red-400 hover:bg-red-500/30',
    muted: 'bg-jeket-silver-muted/20 text-jeket-silver-muted hover:bg-jeket-silver-muted/30',
  };

  const pendingQuestions = questions.filter((q: QAQuestion) => q.status === 'pending');
  const approvedQuestions = questions.filter((q: QAQuestion) => q.status === 'approved');
  const answeredQuestions = questions.filter((q: QAQuestion) => q.status === 'answered');

  return (
    <div className="space-y-6">
      {/* Back + Header */}
      <div className="space-y-4">
        <Link to="/audience/events" className="inline-flex items-center gap-1 text-sm text-jeket-silver-muted hover:text-jeket-gold transition-colors">
          <ArrowLeft size={14} /> Back to Events
        </Link>

        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-jeket-black-elevated flex items-center justify-center">
              <Calendar size={20} className="text-jeket-gold" />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-bold text-jeket-silver-bright">{event.name}</h1>
                <EventStatusBadge status={event.status} />
                <OverrunBadge event={event} />
              </div>
              <ShareableUrl slug={event.slug} />
            </div>
          </div>

          <div className="flex items-center gap-2">
            {lifecycleActions.map((action) => (
              <button
                key={action.label}
                onClick={() => action.mutation.mutate()}
                disabled={action.mutation.isPending}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors disabled:opacity-50',
                  actionVariantStyles[action.variant]
                )}
              >
                <action.icon size={14} />
                {action.mutation.isPending ? '...' : action.label}
              </button>
            ))}
            <button
              onClick={() => {
                if (confirm('Delete this event?')) deleteMutation.mutate();
              }}
              disabled={deleteMutation.isPending}
              className="p-1.5 text-jeket-silver-muted hover:text-red-400 transition-colors disabled:opacity-50"
              title="Delete event"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {actionError && (
          <p role="alert" className="text-sm text-red-400">{actionError}</p>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-jeket-black-elevated">
        {(['config', 'participants', 'qa', 'chat'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2',
              activeTab === tab
                ? 'border-jeket-gold text-jeket-gold'
                : 'border-transparent text-jeket-silver-muted hover:text-jeket-silver-bright'
            )}
          >
            {tab === 'config' && <Settings2 size={14} />}
            {tab === 'participants' && <Users size={14} />}
            {tab === 'qa' && <HelpCircle size={14} />}
            {tab === 'chat' && <MessageSquare size={14} />}
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
            {tab === 'qa' && pendingQuestions.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 text-[10px] rounded-full bg-jeket-gold/20 text-jeket-gold">
                {pendingQuestions.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Config Tab */}
      {activeTab === 'config' && (
        <EditableConfigTab event={event} onSave={invalidateEvent} />
      )}

      {/* Participants Tab */}
      {activeTab === 'participants' && <ParticipantSlotsPanel eventId={event.id} />}

      {/* Q&A Tab */}
      {activeTab === 'qa' && (
        <div className="space-y-6">
          {/* Pending */}
          {pendingQuestions.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-yellow-400 mb-3">Pending ({pendingQuestions.length})</h3>
              <div className="space-y-2">
                {pendingQuestions.map((q: QAQuestion) => (
                  <div key={q.id} className="bg-jeket-black-surface border border-yellow-500/20 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <AuthorName name={q.username} verified={q.verified} className="text-xs text-jeket-gold font-medium" />
                        <p className="text-sm text-jeket-silver-bright mt-1">{q.content}</p>
                        <p className="text-xs text-jeket-silver-muted mt-1">
                          {new Date(q.created_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => approveMutation.mutate(q.id)}
                          className="p-1.5 text-jeket-healthy hover:bg-jeket-healthy/20 rounded transition-colors"
                          title="Approve"
                        >
                          <Check size={14} />
                        </button>
                        <button
                          onClick={() => {
                            setAnsweringId(q.id);
                            setAnswerText('');
                          }}
                          className="p-1.5 text-jeket-info hover:bg-jeket-info/20 rounded transition-colors"
                          title="Answer"
                        >
                          <Send size={14} />
                        </button>
                        <button
                          onClick={() => dismissMutation.mutate(q.id)}
                          className="p-1.5 text-red-400 hover:bg-red-500/20 rounded transition-colors"
                          title="Dismiss"
                        >
                          <XCircle size={14} />
                        </button>
                      </div>
                    </div>
                    {answeringId === q.id && (
                      <div className="mt-3 flex gap-2">
                        <input
                          type="text"
                          value={answerText}
                          onChange={(e) => setAnswerText(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && answerText.trim()) {
                              answerMutation.mutate({ id: q.id, answer: answerText.trim() });
                            }
                          }}
                          placeholder="Type your answer..."
                          className="flex-1 bg-jeket-black-elevated border border-jeket-gold/20 rounded px-3 py-1.5 text-sm text-jeket-silver-bright placeholder:text-jeket-silver-muted/50 focus:outline-none focus:border-jeket-gold/50"
                          autoFocus
                        />
                        <button
                          onClick={() => {
                            if (answerText.trim()) {
                              answerMutation.mutate({ id: q.id, answer: answerText.trim() });
                            }
                          }}
                          disabled={!answerText.trim() || answerMutation.isPending}
                          className="px-3 py-1.5 text-sm bg-jeket-gold/20 text-jeket-gold rounded hover:bg-jeket-gold/30 disabled:opacity-50"
                        >
                          Submit
                        </button>
                        <button
                          onClick={() => setAnsweringId(null)}
                          className="px-2 py-1.5 text-sm text-jeket-silver-muted hover:text-jeket-silver-bright"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Approved */}
          {approvedQuestions.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-jeket-info mb-3">Approved ({approvedQuestions.length})</h3>
              <div className="space-y-2">
                {approvedQuestions.map((q: QAQuestion) => (
                  <div key={q.id} className="bg-jeket-black-surface border border-jeket-info/20 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <AuthorName name={q.username} verified={q.verified} className="text-xs text-jeket-gold font-medium" />
                        <p className="text-sm text-jeket-silver-bright mt-1">{q.content}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => {
                            setAnsweringId(q.id);
                            setAnswerText('');
                          }}
                          className="p-1.5 text-jeket-info hover:bg-jeket-info/20 rounded transition-colors"
                          title="Answer"
                        >
                          <Send size={14} />
                        </button>
                        <button
                          onClick={() => dismissMutation.mutate(q.id)}
                          className="p-1.5 text-red-400 hover:bg-red-500/20 rounded transition-colors"
                          title="Dismiss"
                        >
                          <XCircle size={14} />
                        </button>
                      </div>
                    </div>
                    {answeringId === q.id && (
                      <div className="mt-3 flex gap-2">
                        <input
                          type="text"
                          value={answerText}
                          onChange={(e) => setAnswerText(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && answerText.trim()) {
                              answerMutation.mutate({ id: q.id, answer: answerText.trim() });
                            }
                          }}
                          placeholder="Type your answer..."
                          className="flex-1 bg-jeket-black-elevated border border-jeket-gold/20 rounded px-3 py-1.5 text-sm text-jeket-silver-bright placeholder:text-jeket-silver-muted/50 focus:outline-none focus:border-jeket-gold/50"
                          autoFocus
                        />
                        <button
                          onClick={() => {
                            if (answerText.trim()) {
                              answerMutation.mutate({ id: q.id, answer: answerText.trim() });
                            }
                          }}
                          disabled={!answerText.trim() || answerMutation.isPending}
                          className="px-3 py-1.5 text-sm bg-jeket-gold/20 text-jeket-gold rounded hover:bg-jeket-gold/30 disabled:opacity-50"
                        >
                          Submit
                        </button>
                        <button
                          onClick={() => setAnsweringId(null)}
                          className="px-2 py-1.5 text-sm text-jeket-silver-muted hover:text-jeket-silver-bright"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Answered */}
          {answeredQuestions.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-jeket-healthy mb-3">Answered ({answeredQuestions.length})</h3>
              <div className="space-y-2">
                {answeredQuestions.map((q: QAQuestion) => (
                  <div key={q.id} className="bg-jeket-black-surface border border-jeket-healthy/20 rounded-lg p-4">
                    <AuthorName name={q.username} verified={q.verified} className="text-xs text-jeket-gold font-medium" />
                    <p className="text-sm text-jeket-silver-bright mt-1">{q.content}</p>
                    {q.answer && (
                      <div className="mt-2 pl-3 border-l-2 border-jeket-gold/30">
                        <p className="text-sm text-jeket-silver-bright">{q.answer}</p>
                        <p className="text-xs text-jeket-silver-muted mt-1">
                          Answered by {q.answered_by} {q.answered_at && `at ${new Date(q.answered_at).toLocaleString()}`}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {questions.length === 0 && (
            <div className="text-center py-12">
              <HelpCircle size={48} className="mx-auto text-jeket-silver-muted/30 mb-3" />
              <p className="text-jeket-silver-muted">No questions yet</p>
            </div>
          )}
        </div>
      )}

      {/* Chat Tab */}
      {activeTab === 'chat' && <AdminChatTab event={event} />}
    </div>
  );
}

// ---- Main Component ----

export default function Events() {
  const { eventId } = useParams<{ eventId: string }>();

  if (eventId) {
    return <EventDetail eventId={eventId} />;
  }

  return <EventsList />;
}
